import { Chip, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  CheckCircleFilledOMIcon,
  ClockOMIcon,
  HandOMIcon,
  PauseCircleOMIcon,
  ThumbsDownOMIcon,
  WarningOMIcon,
} from '@omnea-app/icons'
import { getStatusPillLabel } from '@omnea-app/packages/universal/requests/operationalStatusPill'
import type { ChipProps } from '@mui/material'
import type { StatusPillKey } from '@omnea-app/packages/universal/requests/operationalStatusPill'
import type { RequestListItem } from '@omnea-app/requests/requestsHttpClient'
import type { ReactElement, ReactNode } from 'react'

export type OperationalStatus = NonNullable<RequestListItem['derivedFields']['operationalStatus']>

// Matches the status-label frame in Figma (Requests & Approvals, node 11755:21863).
const PILL_STYLE: Record<StatusPillKey, { color: ChipProps['color']; icon?: ReactElement }> = {
  WAITING_FOR_YOU: { color: 'plum', icon: <ClockOMIcon /> },
  BLOCKED: { color: 'orange', icon: <HandOMIcon /> },
  PAUSED: { color: 'neutral', icon: <PauseCircleOMIcon /> },
  NEEDS_ATTENTION: { color: 'warning', icon: <WarningOMIcon /> },
  SLOW: { color: 'white', icon: <ClockOMIcon /> },
  ON_TRACK: { color: 'white' },
  COMPLETED: { color: 'success', icon: <CheckCircleFilledOMIcon /> },
  REJECTED: { color: 'error', icon: <ThumbsDownOMIcon /> },
}

type ChipOnlyProps = Omit<ChipProps, 'color' | 'label' | 'size' | 'sx' | 'icon'> & { pillKey: StatusPillKey }

// Remaining props reach the Chip so it can be a PinnablePopover trigger.
export const OperationalStatusChip = ({ pillKey, ...chipProps }: ChipOnlyProps) => {
  const { t } = useTranslation('requests')
  const { color, icon } = PILL_STYLE[pillKey]
  const label = getStatusPillLabel(pillKey, t)
  return <Chip {...chipProps} size="small" color={color} icon={icon} label={label} aria-label={label} />
}

type WithOverdueDaysProps = {
  status: OperationalStatus
  children: ReactNode
}

// Slow carries how late its step is beside the chip rather than inside it.
export const WithOverdueDays = ({ status: { key, overdueWorkingDays }, children }: WithOverdueDaysProps) => {
  const { t } = useTranslation('requests')
  if (key !== 'SLOW' || overdueWorkingDays === 0) return <>{children}</>
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      {children}
      <Typography variant="small-regular" color="text.muted" aria-hidden>
        ∙
      </Typography>
      <Typography variant="small-regular" color="text.warning.default">
        {t('table-column.status-pill.days-late', { count: overdueWorkingDays })}
      </Typography>
    </Stack>
  )
}
